import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Container,
  Paper,
  Typography,
  Box,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Alert,
  LinearProgress,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Divider,
  CircularProgress,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Timer as TimerIcon,
  Assignment as AssignmentIcon,
  School as SchoolIcon,
  Close as CloseIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import { examAPI } from '../services/api';

const ExamResults = () => {
  const { examId } = useParams();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [examToDelete, setExamToDelete] = useState(null);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [selectedExam, setSelectedExam] = useState(null);
  const [examDetails, setExamDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [openDetails, setOpenDetails] = useState(false);

  useEffect(() => {
    fetchResults();
  }, []);

  const fetchResults = async () => {
    try {
      setLoading(true);
      setError(null);
      let response;
      
      if (user.role === 'admin' || user.role === 'super_admin') {
        response = await examAPI.getAllResults();
        console.log('Admin results response:', response);
        if (response.data && response.data.results) {
          setResults(response.data.results);
        } else {
          setResults([]);
        }
      } else {
        response = await examAPI.getStudentResults(user.id);
        console.log('Student results response:', response);
        if (response.data && response.data.success) {
          setResults(response.data.data);
        } else {
          setResults([]);
        }
      }
    } catch (err) {
      console.error('Error fetching results:', err);
      setError('Failed to fetch results. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (exam) => {
    setExamToDelete(exam);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      const response = await examAPI.deleteExam(examToDelete.id);
      if (response.data.success) {
        // Remove the deleted exam from the results
        setResults(results.filter(result => result.id !== examToDelete.id));
        setDeleteDialogOpen(false);
        setExamToDelete(null);
      } else {
        setError(response.data.message || 'Failed to delete exam');
      }
    } catch (error) {
      console.error('Error deleting exam:', error);
      setError(error.response?.data?.message || 'Failed to delete exam');
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setExamToDelete(null);
  };

  const handleStudentClick = (student) => {
    setSelectedStudent(student);
    setSelectedExam(null);
    setExamDetails(null);
  };

  const handleBack = () => {
    setSelectedStudent(null);
    setSelectedExam(null);
    setExamDetails(null);
  };

  const handleExamClick = async (exam) => {
    setSelectedExam(exam);
    setLoadingDetails(true);
    try {
      const response = await examAPI.getStudentExamDetails(exam.attempt_id);
      if (response.data && response.data.data) {
        setExamDetails(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching exam details:', err);
      setError('Failed to fetch exam details. Please try again.');
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleCloseExamDetails = () => {
    setSelectedExam(null);
    setExamDetails(null);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  const formatScore = (score) => {
    if (score === null || score === undefined) return 'N/A';
    return typeof score === 'number' ? score.toFixed(2) : 'N/A';
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'success.main';
    if (score >= 60) return 'warning.main';
    return 'error.main';
  };

  const getStatusChip = (status) => {
    switch (status) {
      case 'completed':
        return (
          <Chip
            icon={<CheckCircleIcon />}
            label="Completed"
            color="success"
            size="small"
          />
        );
      case 'in_progress':
        return (
          <Chip
            icon={<TimerIcon />}
            label="In Progress"
            color="warning"
            size="small"
          />
        );
      default:
        return (
          <Chip
            icon={<CancelIcon />}
            label={status}
            color="error"
            size="small"
          />
        );
    }
  };

  const handleDownloadPDF = async (examId) => {
    try {
      const response = await examAPI.downloadExamPDF(examId);
      // Create a blob from the PDF data
      const blob = new Blob([response.data], { type: 'application/pdf' });
      // Create a URL for the blob
      const url = window.URL.createObjectURL(blob);
      // Create a temporary link element
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `exam_${examId}.pdf`);
      // Append to the document
      document.body.appendChild(link);
      // Trigger the download
      link.click();
      // Clean up
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      setError('Failed to download PDF. Please try again.');
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  if (user.role === 'admin' || user.role === 'super_admin') {
    if (selectedStudent) {
      return (
        <Container maxWidth="lg" sx={{ mt: 4 }}>
          <Box mb={3}>
            <Button 
              variant="outlined" 
              onClick={handleBack} 
              sx={{ mb: 2 }}
              startIcon={<SchoolIcon />}
            >
              Back to Students
            </Button>
            <Typography variant="h4" gutterBottom sx={{ color: 'primary.main' }}>
              Results for {selectedStudent.student_name}
            </Typography>
          </Box>
          <TableContainer 
            component={Paper} 
            sx={{ 
              boxShadow: 3,
              borderRadius: 2,
              overflow: 'hidden'
            }}
          >
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: 'primary.main' }}>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Exam Title</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Score</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Status</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Time Taken</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Submission Time</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {selectedStudent.exams.map((exam) => (
                  <TableRow key={exam.attempt_id}>
                    <TableCell>{exam.exam_title}</TableCell>
                    <TableCell>
                      <Typography
                        sx={{
                          color: getScoreColor(exam.score),
                          fontWeight: 'bold'
                        }}
                      >
                        {formatScore(exam.score)}%
                      </Typography>
                    </TableCell>
                    <TableCell>{getStatusChip(exam.status)}</TableCell>
                    <TableCell>{exam.time_taken} minutes</TableCell>
                    <TableCell>{formatDate(exam.end_time)}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleExamClick(exam)}
                          >
                            <AssignmentIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Download PDF">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleDownloadPDF(exam.exam_id)}
                          >
                            <DownloadIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Dialog 
            open={!!selectedExam} 
            onClose={handleCloseExamDetails}
            maxWidth="md"
            fullWidth
            PaperProps={{
              sx: {
                borderRadius: 2,
                boxShadow: 6
              }
            }}
          >
            <DialogTitle sx={{ 
              backgroundColor: 'primary.main', 
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}>
              <AssignmentIcon />
              Exam Details - {selectedExam?.exam_title}
            </DialogTitle>
            <DialogContent sx={{ mt: 2 }}>
              {loadingDetails ? (
                <Box display="flex" justifyContent="center" p={3}>
                  <CircularProgress />
                </Box>
              ) : examDetails ? (
                <Box>
                  <Paper 
                    elevation={3} 
                    sx={{ 
                      p: 3, 
                      mb: 3,
                      backgroundColor: 'background.default'
                    }}
                  >
                    <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
                      Summary
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="subtitle2" color="text.secondary">
                            Score
                          </Typography>
                          <Typography 
                            variant="h4" 
                            sx={{ 
                              color: getScoreColor(examDetails.score_percentage),
                              fontWeight: 'bold'
                            }}
                          >
                            {formatScore(examDetails.score_percentage)}%
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="subtitle2" color="text.secondary">
                            Time Taken
                          </Typography>
                          <Typography variant="body1">
                            {examDetails.time_taken || 0} minutes
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="subtitle2" color="text.secondary">
                            Status
                          </Typography>
                          {getStatusChip(examDetails.status)}
                        </Box>
                        <Box>
                          <Typography variant="subtitle2" color="text.secondary">
                            Submitted
                          </Typography>
                          <Typography variant="body1">
                            {examDetails.end_time ? new Date(examDetails.end_time).toLocaleString() : 'N/A'}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </Paper>

                  <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
                    Question Details
                  </Typography>
                  <TableContainer 
                    component={Paper} 
                    sx={{ 
                      boxShadow: 3,
                      borderRadius: 2,
                      overflow: 'hidden'
                    }}
                  >
                    <Table>
                      <TableHead>
                        <TableRow sx={{ backgroundColor: 'primary.main' }}>
                          <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Question</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Student's Answer</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Correct Answer</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Points Earned</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Total Points</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {examDetails.questions?.map((question) => (
                          <TableRow 
                            key={question.question_id}
                            sx={{ 
                              '&:hover': { backgroundColor: 'action.hover' },
                              backgroundColor: question.is_correct ? 'success.lighter' : 'error.lighter'
                            }}
                          >
                            <TableCell>{question.question_text}</TableCell>
                            <TableCell>
                              <Box display="flex" alignItems="center">
                                {question.selected_answer ? (
                                  <Box>
                                    <Typography>
                                      {question.options.find(opt => opt.letter === question.selected_answer)?.text || 'N/A'}
                                    </Typography>
                                    {question.is_correct ? (
                                      <CheckCircleIcon sx={{ ml: 1, color: 'success.main' }} />
                                    ) : (
                                      <CancelIcon sx={{ ml: 1, color: 'error.main' }} />
                                    )}
                                  </Box>
                                ) : (
                                  'Not answered'
                                )}
                              </Box>
                            </TableCell>
                            <TableCell>
                              {question.options.find(opt => opt.letter === question.correct_answer)?.text || 'N/A'}
                            </TableCell>
                            <TableCell>
                              <Typography 
                                sx={{ 
                                  color: question.is_correct ? 'success.main' : 'error.main',
                                  fontWeight: 'bold'
                                }}
                              >
                                {formatScore(question.points_earned)}
                              </Typography>
                            </TableCell>
                            <TableCell>{formatScore(question.weight_percentage)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              ) : (
                <Alert severity="error">
                  Failed to load exam details
                </Alert>
              )}
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
              <Button 
                onClick={handleCloseExamDetails}
                variant="contained"
                color="primary"
              >
                Close
              </Button>
            </DialogActions>
          </Dialog>
        </Container>
      );
    }

    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Typography 
          variant="h4" 
          gutterBottom 
          sx={{ 
            color: 'primary.main',
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}
        >
          <SchoolIcon />
          Student Exam Results
        </Typography>
        <Grid container spacing={3}>
          {results.map((student) => (
            <Grid item xs={12} sm={6} md={4} key={student.student_id}>
              <Card 
                sx={{ 
                  cursor: 'pointer',
                  '&:hover': {
                    boxShadow: 6,
                    transform: 'translateY(-4px)',
                    transition: 'all 0.3s'
                  }
                }}
                onClick={() => handleStudentClick(student)}
              >
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
                    {student.student_name}
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Exams Taken
                    </Typography>
                    <Typography variant="h5" sx={{ color: 'primary.main' }}>
                      {student.total_exams || 0}
                    </Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Average Score
                    </Typography>
                    <Typography 
                      variant="h5" 
                      sx={{ 
                        color: getScoreColor(student.average_score),
                        fontWeight: 'bold'
                      }}
                    >
                      {formatScore(student.average_score)}%
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Last Exam
                    </Typography>
                    <Typography variant="body1">
                      {student.last_exam_date ? new Date(student.last_exam_date).toLocaleDateString() : 'N/A'}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
    );
  }

  // Student view
  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          Exam Results
        </Typography>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        ) : (
          <Box>
            {results.map((attempt) => (
              <Paper key={attempt.attempt_id} sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  {attempt.exam_title}
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {attempt.exam_description}
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                  <Chip
                    icon={<AssignmentIcon />}
                    label={`Score: ${formatScore(attempt.score_percentage)}%`}
                    color={getScoreColor(attempt.score_percentage)}
                  />
                  <Chip
                    icon={<TimerIcon />}
                    label={`Time: ${attempt.time_taken || 0} minutes`}
                  />
                  <Chip
                    icon={<SchoolIcon />}
                    label={`Status: ${attempt.status}`}
                    color={attempt.status === 'completed' ? 'success' : 'warning'}
                  />
                </Box>

                <TableContainer component={Paper} sx={{ mt: 3 }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Question</TableCell>
                        <TableCell>Your Answer</TableCell>
                        <TableCell>Correct Answer</TableCell>
                        <TableCell>Points Earned</TableCell>
                        <TableCell>Total Points</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {attempt.questions?.map((question) => (
                        <TableRow key={question.question_id}>
                          <TableCell>{question.question_text}</TableCell>
                          <TableCell>
                            {question.options.find(opt => opt.letter === question.selected_answer)?.text || 'N/A'}
                          </TableCell>
                          <TableCell>
                            {question.options.find(opt => opt.letter === question.correct_answer)?.text || 'N/A'}
                          </TableCell>
                          <TableCell>
                            <Typography
                              sx={{
                                color: question.is_correct ? 'success.main' : 'error.main',
                                fontWeight: 'bold'
                              }}
                            >
                              {formatScore(question.points_earned)}
                            </Typography>
                          </TableCell>
                          <TableCell>{formatScore(question.weight_percentage)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            ))}
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default ExamResults;